import React, { useState } from "react";
import { Button, Input, Modal, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { Header } from "../../../../components";
import { useNewWorkSound } from "../../../../hooks/useNewWorkSound";
import EditModal from "../../Input/components/EditModal";
import RejectModal from "../../Input/components/RejectModal";
import DetailsModal from "../../Input/components/DetailsModal";
import {
  useClaimLineCase,
  useCloseLineCase,
  useFetchLineCaseQueue,
  useReleaseLineCase,
  useResendLineCase,
} from "../hooks/useFetch";

const { Text } = Typography;

const errorText = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

/**
 * เคสจาก LINE ที่หลังบ้านตีกลับ (Reject) หรือขอข้อมูลเพิ่ม (Inform)
 *
 * คนสร้างเคสพวกนี้คือ user `system` ทีม Input ตอบลูกค้าไม่ได้ จึงแยกออกมาจากหน้า Input
 * แอดมินไปคุยกับลูกค้าในแชท OA แล้วกลับมากด "ส่งกลับเข้าคิว" (ได้ข้อมูลเพิ่ม) หรือ "ปิดเรื่อง"
 * ลูกค้าเห็นผลในหน้าเช็คสถานะของ LIFF เอง: ส่งกลับ = "กำลังตรวจสอบ" · ปิด = "ปิดเรื่องแล้ว"
 *
 * ต้อง "รับเรื่อง" ก่อนถึงจะกดส่งกลับ/ปิดได้ — กันสองคนทักลูกค้าคนเดียวกันแล้วตอบไม่ตรงกัน
 */
function LineCasePage() {
  const userId = sessionStorage.getItem("id");
  const { data = [], isLoading, refetch } = useFetchLineCaseQueue();
  const { mutateAsync: claim, isLoading: isClaiming } = useClaimLineCase();
  const { mutateAsync: release } = useReleaseLineCase();
  const { mutateAsync: resend, isLoading: isResending } = useResendLineCase();
  const { mutateAsync: close, isLoading: isClosing } = useCloseLineCase();

  const [search, setSearch] = useState("");
  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [closeRecord, setCloseRecord] = useState(null);

  // นับจากรายการเต็มที่ยังไม่ผ่านช่องค้นหา (ดูเหตุผลใน useNewWorkSound)
  useNewWorkSound(isLoading ? null : data.length);

  const isMine = (r) => r.line?.claimedBy && String(r.line.claimedBy) === String(userId);

  const doClaim = async (record, force = false) => {
    try {
      await claim({ id: record.id, force });
      message.success(`รับเรื่องเคส #${record.id} แล้ว`);
    } catch (error) {
      message.error(errorText(error, "รับเรื่องไม่สำเร็จ"));
    } finally {
      refetch();
    }
  };

  // รับต่อจากคนอื่น — ถามก่อน เพราะคนเดิมอาจกำลังพิมพ์คุยกับลูกค้าอยู่
  const confirmTakeOver = (record) =>
    Modal.confirm({
      title: `รับเรื่องต่อจาก ${record.line?.claimedByName || "คนอื่น"}?`,
      content: "ถ้าเขายังคุยกับลูกค้าอยู่ ลูกค้าจะได้คำตอบจากสองคน — ควรแจ้งเขาก่อน",
      okText: "รับเรื่องแทน",
      cancelText: "ยกเลิก",
      onOk: () => doClaim(record, true),
    });

  // เคสออกจากคิวแล้ว → ล้างชื่อผู้รับเรื่อง (ถ้าพลาดก็แค่ชื่อค้าง ไม่กระทบเคส จึงไม่แจ้ง error)
  const releaseQuietly = async (id) => {
    try {
      await release(id);
    } catch (error) {
      /* ไม่ต้องแจ้ง */
    }
  };

  const handleResend = async (formattedValues) => {
    try {
      await resend(formattedValues);
      message.success("ส่งกลับเข้าคิวแล้ว");
      await releaseQuietly(formattedValues.id);
    } catch (error) {
      message.error(errorText(error, "ส่งกลับเข้าคิวไม่สำเร็จ"));
    } finally {
      setEditRecord(null);
      refetch();
    }
  };

  const handleClose = async (formattedValues) => {
    try {
      await close({
        caseId: closeRecord.id,
        // RejectModal ส่งเหตุผลมาในชื่อ rejectRemark แต่ API ปิดเคสรับชื่อ cancelRemark
        cancelRemark: formattedValues.rejectRemark,
        cancel_By: userId,
      });
      message.success("ปิดเรื่องแล้ว");
      await releaseQuietly(closeRecord.id);
    } catch (error) {
      message.error(errorText(error, "ปิดเรื่องไม่สำเร็จ"));
    } finally {
      setCloseRecord(null);
      refetch();
    }
  };

  const keyword = search.trim().toLowerCase();
  const rows = keyword
    ? data.filter((r) =>
        [r.id, r.userId, r.note, r.line?.lineName, r.line?.phone, r.line?.oaName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(keyword))
      )
    : data;

  const columns = [
    {
      title: "เคส",
      key: "case",
      width: 110,
      render: (_, r) => (
        <div>
          <div className="font-semibold">#{r.id}</div>
          <div className="text-xs text-gray-500">{r.caseName}</div>
        </div>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      width: 120,
      render: (status) =>
        status === "Inform" ? <Tag color="orange">ขอข้อมูลเพิ่ม</Tag> : <Tag color="red">ตีกลับ</Tag>,
    },
    {
      title: "OA",
      key: "oa",
      width: 130,
      render: (_, r) => r.line?.oaName || <Text type="secondary">-</Text>,
    },
    {
      title: "ลูกค้า",
      key: "customer",
      width: 190,
      render: (_, r) => (
        <div>
          <div>{r.line?.lineName || "-"}</div>
          {r.line?.phone && (
            <Text copyable className="text-xs">
              {r.line.phone}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "เหตุผลจากหลังบ้าน",
      key: "reason",
      render: (_, r) => {
        const reason = r.status === "Inform" ? r.inform_remark : r.reject_remark;
        return <div style={{ whiteSpace: "pre-wrap", minWidth: 200 }}>{reason || "-"}</div>;
      },
    },
    {
      title: "อัปเดตล่าสุด",
      dataIndex: "lastUpdateDate",
      width: 150,
    },
    {
      title: "ผู้รับเรื่อง",
      key: "claim",
      width: 150,
      render: (_, r) =>
        r.line?.claimedBy ? (
          <div>
            <Tag color={isMine(r) ? "green" : "blue"}>{isMine(r) ? "ฉัน" : r.line.claimedByName || "-"}</Tag>
            <div className="text-xs text-gray-500">{r.line.claimedAt}</div>
          </div>
        ) : (
          <Text type="secondary">ยังไม่มีคนรับ</Text>
        ),
    },
    {
      title: "จัดการ",
      key: "actions",
      fixed: "right",
      width: 230,
      render: (_, r) => {
        const claimedByOther = r.line?.claimedBy && !isMine(r);
        return (
          <Space wrap size={4}>
            <Button size="small" onClick={() => setDetailRecord(r)}>
              รายละเอียด
            </Button>
            {!r.line?.claimedBy && (
              <Button size="small" type="primary" loading={isClaiming} onClick={() => doClaim(r)}>
                รับเรื่อง
              </Button>
            )}
            {claimedByOther && (
              <Button size="small" disabled={isClaiming} onClick={() => confirmTakeOver(r)}>
                รับเรื่องแทน
              </Button>
            )}
            {isMine(r) && (
              <>
                <Button size="small" type="primary" onClick={() => setEditRecord(r)}>
                  ส่งกลับเข้าคิว
                </Button>
                <Tooltip title="ลูกค้าจะเห็นว่า ปิดเรื่องแล้ว — แอดมินจะแจ้งรายละเอียดในแชท">
                  <Button size="small" danger onClick={() => setCloseRecord(r)}>
                    ปิดเรื่อง
                  </Button>
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="md:m-5">
      <div className="con flex-col gap-6" style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        <Header category="App" title="เคสจาก LINE" />
        <div className="mb-3 text-sm text-gray-500">
          เคสที่ลูกค้าแจ้งผ่าน LINE แล้วหลังบ้านตีกลับหรือขอข้อมูลเพิ่ม — กดรับเรื่อง ไปคุยกับลูกค้าในแชท OA
          แล้วกลับมากดส่งกลับเข้าคิว (ได้ข้อมูลเพิ่มแล้ว) หรือปิดเรื่อง
        </div>
        <div className="mb-4">
          <Input
            placeholder="ค้นหา เลขเคส / ชื่อ LINE / เบอร์ / OA"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="w-full sm:w-80"
          />
        </div>
        <div className="w-full overflow-x-auto" style={{ minWidth: 0, maxWidth: "100%" }}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={isLoading}
            bordered
            size="middle"
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: "ไม่มีเคสจาก LINE ที่รอแอดมินตอบ" }}
          />
        </div>
      </div>

      <DetailsModal visible={!!detailRecord} onClose={() => setDetailRecord(null)} record={detailRecord} />
      <EditModal
        visible={!!editRecord}
        onCancel={() => setEditRecord(null)}
        onSubmit={handleResend}
        record={editRecord}
        loading={isResending}
      />
      <RejectModal
        visible={!!closeRecord}
        onCancel={() => setCloseRecord(null)}
        onSubmit={handleClose}
        record={closeRecord}
        loading={isClosing}
      />
    </div>
  );
}

export default LineCasePage;
